import { FC, useEffect, useRef, useState } from "react";
import { Avatar, Box, Button, IconButton, Modal, Stack, Typography } from "@mui/material";
import CloseSvg from "@/assets/svg/CloseSvg";
import { styles } from "./stylesEditCommentModal";
import { CompositeDecorator, ContentState, Editor, EditorState, Modifier } from "draft-js";
import EmojiBlackSvg from "@/assets/svg/EmojiBlackSvg";
import EmojiPicker from "emoji-picker-react";
import { formatTimeAgo } from "@/helpers/getHoursDifference";

interface IEditCommentModalProps {
  open: boolean;
  handleClose: () => void;
  uid: string;
  openEditModal: any;
  postId?: string;
  edit: any;
  handleUpdate: any;

  parentComment?: {
    avatar: string;
    username: string;
    comment: string;
    createdAt: string;
  };
  avatar: string;
}

const MentionSpan = (props: any) => {
  return <span style={styles.mentionStyle}>{props.children}</span>;
};

const findMentionEntities = (contentBlock: any, callback: any) => {
  const text = contentBlock.getText();
  const regex = /@\w+/g;
  let matchArr, start;
  while ((matchArr = regex.exec(text)) !== null) {
    start = matchArr.index;
    callback(start, start + matchArr[0].length);
  }
};

const decorator = new CompositeDecorator([
  {
    strategy: findMentionEntities,
    component: MentionSpan,
  },
]);
const MAX_LENGTH = 1000;

const EditCommentModal: FC<IEditCommentModalProps> = ({
  open,
  handleClose,
  edit,
  handleUpdate,
  parentComment,
  avatar,
}) => {
  const [showPicker, setShowPicker] = useState(false);
  const [editorState, setEditorState] = useState(EditorState.createEmpty(decorator));
  const pickerRef = useRef<HTMLDivElement | null>(null);
  const emojiButtonRef = useRef<HTMLDivElement | null>(null);
  const [pickerPosition, setPickerPosition] = useState({
    top: "100%",
    left: "0px",
    bottom: "auto",
    right: "auto",
  });

  const handleEmojiClick = (emojiData: any) => {
    const contentState = editorState.getCurrentContent();
    const selectionState = editorState.getSelection();

    const newContentState = Modifier.insertText(contentState, selectionState, emojiData.emoji);

    const newEditorState = EditorState.push(editorState, newContentState, "insert-characters");

    setEditorState(newEditorState);
    setShowPicker(false);
  };
  const handleChange = (newState: EditorState) => {
    setEditorState(newState);
  };

  const togglePicker = () => {
    if (emojiButtonRef.current) {
      const rect = emojiButtonRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      setPickerPosition({
        top: spaceBelow < 400 ? "auto" : "100%",
        bottom: spaceBelow < 400 ? "100%" : "auto",
        left: "0px",
        right: "auto",
      });
    }

    setShowPicker((prev) => !prev);
  };

  const HighlightMentions: FC<{ text: string }> = ({ text }) => {
    const regex = /@[\w\d_-]+/g;
    const parts = text.split(regex);
    const matches = text.match(regex);

    const elements: React.ReactNode[] = [];
    parts.forEach((part, i) => {
      elements.push(part);
      if (matches && matches[i]) {
        elements.push(
          <span key={i} style={styles.mentionStyle}>
            {matches[i]}
          </span>
        );
      }
    });
    return <>{elements}</>;
  };

  useEffect(() => {
    if (edit?.comment) {
      const contentState = ContentState.createFromText(edit.comment);
      const newEditorState = EditorState.createWithContent(contentState, decorator);
      setEditorState(newEditorState);
    }
  }, [edit]);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={parentComment ? styles.modalReply : styles.modalBase}>
        <Box sx={styles.header}>
          <Typography sx={styles.title}>{parentComment ? "Edit reply" : "Edit comment"}</Typography>
          <IconButton
            onClick={handleClose}
            size="small"
            aria-label="close"
            sx={parentComment ? styles.closeButtonReply : styles.closeButtonBase}
          >
            <CloseSvg />
          </IconButton>
        </Box>

        {parentComment && (
          <Box sx={styles.parentContainer}>
            <Box sx={styles.parentHeader}>
              <Avatar sx={styles.parentAvatar} src={parentComment.avatar} />
              <Box sx={styles.parentUserInfo}>
                <Typography sx={styles.parentUsername}>{parentComment.username}</Typography>
                <Typography sx={styles.parentTime}>
                  • {formatTimeAgo(parentComment.createdAt)}.
                </Typography>
              </Box>
            </Box>
            <Box sx={styles.parentCommentBox}>
              <Box sx={styles.parentCommentInner}>
                <Typography sx={styles.parentCommentText}>
                  <HighlightMentions text={parentComment.comment} />
                </Typography>
              </Box>
            </Box>
          </Box>
        )}

        <Stack direction="row" gap="0.75rem">
          {parentComment && <Avatar sx={styles.userAvatar} src={avatar} />}

          <Box sx={{ ...styles.editorContainer, mt: parentComment ? 0 : "24px" }}>
            <Box className="hide-scrollbar" sx={styles.editorInner}>
              <Editor
                editorState={editorState}
                onChange={handleChange}
                placeholder="Type something with @mention"
              />
            </Box>

            <Stack
              sx={styles.editorBottomBar}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Stack sx={styles.emojiStack}>
                <Box ref={emojiButtonRef} display="inline-block">
                  <EmojiBlackSvg onClick={togglePicker} cursor="pointer" />
                </Box>

                {showPicker && (
                  <Box
                    ref={pickerRef}
                    sx={{
                      position: "absolute",
                      zIndex: 10,
                      ...pickerPosition,
                    }}
                  >
                    <EmojiPicker onEmojiClick={handleEmojiClick} />
                  </Box>
                )}
                {/* <UploadImageSvg /> */}
              </Stack>

              <Box sx={styles.counterAndButtonBox}>
                {MAX_LENGTH - editorState.getCurrentContent().getPlainText().length >= 0 && (
                  <Typography sx={styles.counter} variant="body2">
                    {MAX_LENGTH - editorState.getCurrentContent().getPlainText().length}
                  </Typography>
                )}

                {editorState.getCurrentContent().getPlainText().length > MAX_LENGTH && (
                  <Typography variant="body2" color="error">
                    Character limit exceeded
                  </Typography>
                )}

                <Button
                  variant="contained"
                  disabled={
                    editorState.getCurrentContent().getPlainText().length === 0 ||
                    editorState.getCurrentContent().getPlainText().length > MAX_LENGTH
                  }
                  onClick={() => {
                    handleUpdate(edit.uid, editorState.getCurrentContent().getPlainText().trim());
                    handleClose();
                  }}
                  sx={styles.button}
                >
                  {parentComment ? "Send" : "Update"}
                </Button>
              </Box>
            </Stack>
          </Box>
        </Stack>
      </Box>
    </Modal>
  );
};

export default EditCommentModal;
